// Yes / No quick replies for AI answers.
//
// Two sources, in priority order. The backend (utils/proceed_prompt.py in
// IGIGMomentumMyAMP) applies the same two to the terminal answer event of
// /claude-managed/chat and /momentum-kanban-stream, and that decision wins
// for live rows; this file covers rows that carry no decision (history rows,
// older backends, surfaces that do not read the flag). Keep the two in step.
//   1. an explicit $$SHOWMODAL_<text><Buttons>[A|B]$$ marker from the AI;
//   2. a closing yes/no question about proceeding ("Do you want me to
//      proceed?", "Shall I continue?", "Proceed?") - asksToProceed().

export type QuickReplyOption = { label: string; reply: string };

// ── Explicit button marker from the AI ───────────────────────────────────────
// $$SHOWMODAL_<question text><Buttons>[YES|NO]$$
//   e.g. $$SHOWMODAL_Do you want me to send this email?<Buttons>[YES|NO]$$
//        $$SHOWMODAL_How should I file this?<Buttons>[Renewal|Endorsement|Other]$$
// The marker is removed from the displayed text; its question renders above one
// button per label, and clicking a button sends that label back as the reply.
const QUICK_PROMPT_RE = /\$\$SHOWMODAL_([\s\S]*?)<\s*Buttons\s*>\s*\[([^\]]*)\]\s*\$\$/gi;
const QUICK_PROMPT_OPEN = '$$SHOWMODAL_';

export type AiQuickPrompt = { text: string; options: QuickReplyOption[] };

// While a reply streams, the marker arrives in pieces — hide an unterminated
// one (or a trailing fragment of its opener, e.g. "$$SHOW") so raw marker text
// never flashes on screen before it is complete. Streaming only: a finished
// message ending in "$$" is another marker's closer (charts etc.), not ours.
const stripPartialQuickPrompt = (content: string): string => {
  const upper = content.toUpperCase();
  const openAt = upper.lastIndexOf(QUICK_PROMPT_OPEN);
  if (openAt !== -1) return content.slice(0, openAt).trimEnd();
  for (let len = QUICK_PROMPT_OPEN.length - 1; len >= 3; len--) {
    if (upper.endsWith(QUICK_PROMPT_OPEN.slice(0, len))) {
      return content.slice(0, content.length - len).trimEnd();
    }
  }
  return content;
};

/**
 * Pulls $$SHOWMODAL_…$$ markers out of an AI message. Returns the text to
 * display (markers removed) and the last well-formed prompt, if any. Content
 * without a marker comes back untouched.
 */
export const parseQuickPrompt = (
  content: string | null | undefined,
  streaming = false,
): { cleaned: string; prompt: AiQuickPrompt | null } => {
  const raw = content ?? '';
  if (!raw.includes('$$')) return { cleaned: raw, prompt: null };
  let prompt: AiQuickPrompt | null = null;
  let cleaned = raw.replace(QUICK_PROMPT_RE, (_m, text: string, labels: string) => {
    const options = labels
      .split('|')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((label) => ({ label, reply: label }));
    if (options.length) prompt = { text: text.trim(), options };
    return '';
  });
  if (streaming) cleaned = stripPartialQuickPrompt(cleaned);
  return { cleaned: cleaned === raw ? raw : cleaned.trim(), prompt };
};

/** Plain-text previews (cards, snippets): a marker becomes just its question text. */
export const quickPromptMarkersToText = (content: string): string =>
  content.replace(QUICK_PROMPT_RE, (_m, text: string) => text.trim());

// ── Closing go-ahead question ────────────────────────────────────────────────
// asksToProceed() is true when the reply's closing sentence is a yes/no
// question about going ahead ("Do you want me to proceed?", "Shall I
// continue?", "Proceed?") AND nothing in the closing lines asks the user to
// type a value instead. "Yes, proceed." is the wrong reply whenever the agent
// needs a value; the canonical miss is a carrier-portal login: "Could you
// share the 6-digit code sent to your phone so I can proceed?" must leave the
// user the text box, not a Yes button that sends "Yes, proceed." in place of
// the code. Only the closing sentence counts for the positive match, so a
// reply that merely mentions "proceed" mid-way doesn't get the buttons.
//
// Stand-down rules, checked over the closing sentence plus the lines just
// above it (the last TAIL_LINES non-empty lines) — same set as the backend:
//   - open-ended question: what / which / who / when / where / how / why,
//     "tell me which ...", or alternatives ("the home or the auto policy?";
//     "or not" and ", or would you rather ...?" stay yes/no);
//   - hand-over request: "could you provide / share / paste / send", "please
//     enter ...", "send me ...", "reply with the ...", "once you have the
//     ...", "I'll need the ...", "waiting for the ...", "paste it here";
//   - pending login challenge: 2FA / MFA / OTP / verification code / passcode
//     / PIN / password / "the code sent to your phone" in the closing sentence,
//     or elsewhere in the tail next to wording that says it is outstanding
//     ("sent to your phone", "asking for", "do you have", "please enter");
//   - "Yes, proceed." would not mean yes: "should I wait / hold off ...?",
//     "... anything else?".
// Regexes stay lookbehind- and dotAll-free so the es5 bundle and older Safari
// parse them.

// How many closing (non-empty) lines are inspected for "please type
// something" wording. The closing question is always one of them.
export const TAIL_LINES = 4;

// App metadata blocks appended to answers ($$INSUREDCLIENTIDSTART$$...$$INSUREDCLIENTIDEND$$,
// $$POLICYIDSTART$$..., $$IMAGELINKSTART$$..., $$CANGENERATEARTIFACTSTART$$...).
// Never user-visible, so they must not hide the closing question.
const META_BLOCK_RE = /\$\$[A-Z_]+START\$\$[\s\S]*?\$\$[A-Z_]+END\$\$/g;
// Entity links ($$IDF_Name##guid$$ / $$PDF_POL-1##guid$$) render as their display text.
const ENTITY_LINK_RE = /\$\$(?:IDF|PDF)_([\s\S]*?)##[^$]*\$\$/g;
const HTML_TAG_RE = /<[^>]+>/g;
const MD_PUNCT_RE = /[*_`~>#]+/g;
// Sentence boundary: terminator followed by whitespace. A fragment ending in a
// short dotted token ("Mr.", "St.", "No.", "e.g.", "vs.") is an abbreviation,
// not a boundary, and is glued back onto the next fragment.
const SENTENCE_END_RE = /([.!?])\s+/g;
const SENTENCE_MARK = '\u0000';
const ABBREVIATION_END_RE = /(?:^|[\s(["'])(?:[A-Za-z]{1,3}|[A-Za-z]\.[A-Za-z])\.$/;
// The closing sentence asks a yes/no question about going ahead.
const PROCEED_QUESTION_RE = /\b(?:proceed|go ahead|move forward|continue|carry on|get started)\b[^?]*\?$/i;

// Stand-down rules: the answer wants a value typed, not a yes / no. Each rule
// is deliberately narrow about WHO acts: "Shall I send it?" (the agent acts,
// yes/no) must survive; "Could you send me the code?" (the user hands
// something over) must not.
const WH = String.raw`(?:what|which|who|whom|whose|when|where|why|how)`;
// Open-ended question: a wh-word heads the sentence, optionally after a short
// lead-in ("If so, which ...", "Great - how ...") or discourse words. The
// lead-in is capped so "Do you want me to proceed with the quote, when ready?"
// keeps its "Do you want me to" head.
const WH_QUESTION_RE = new RegExp(
  String.raw`^(?:[^.!?]{0,25}?[,:;\-\u2013\u2014]\s+)?` +
    String.raw`(?:(?:so|and|but|or|now|ok(?:ay)?|also|then|please|otherwise|alternatively)\s+)*` +
    WH +
    String.raw`\b[\s\S]*\?$`,
  'i',
);
// ... or the user is asked to say which / what / how ("tell me which policy",
// "let me know what date", "confirm which carrier"). "tell me if / whether" is
// a yes/no and is not matched (the wh-word list has no "if"/"whether").
const WH_EMBEDDED_RE = new RegExp(
  String.raw`\b(?:tell\s+(?:me|us)|let\s+(?:me|us)\s+know|specify|clarify|indicate|confirm|` +
    String.raw`choose|pick|select|decide)\s+(?:on\s+)?` +
    WH +
    String.raw`\b`,
  'i',
);
// "A or B?" offers alternatives Yes / No cannot answer. "or not" and a second
// yes/no clause (", or would you rather review it first?") keep it boolean.
const ALTERNATIVES_RE =
  /\bor\b(?!\s+(?:not|no|should|shall|would|will|do|does|did|can|could|may|might|is|are|am|was|were|have|has|had|if|whether)\b)/i;
// "Yes, proceed." would not mean yes: the agent proposes to wait / hold off.
const HOLD_RE =
  /\b(?:wait|hold\s+off|hold|pause|postpone|delay|defer|refrain|rather\s+not|not\s+(?:yet\s+)?proceed|don'?t\s+proceed)\b/i;
// "Would you like me to continue with anything else?" is a sign-off, not a go-ahead.
const ANYTHING_ELSE_RE = /\b(?:anything|something|nothing|what)\s+(?:else|more|further)\b|\belse\b/i;

// Verbs that hand something to the agent. The lookahead keeps "give me the
// go-ahead" / "tell me if" / "send me a yes" out: those are still yes/no.
const SUPPLY_VERBS =
  String.raw`(?:provide|enter|share|send|paste|type|give|supply|input|forward|relay|text|tell|` +
  String.raw`read|pass|upload|attach|drop|post|submit|fill\s+in|key\s+in|hand|shoot)`;
const SUPPLY_GERUNDS =
  String.raw`(?:providing|entering|sharing|sending|pasting|typing|giving|supplying|inputting|` +
  String.raw`forwarding|relaying|texting|telling|reading|passing|uploading|attaching|dropping|` +
  String.raw`posting|submitting|filling\s+in|keying\s+in)`;
const BOOLEAN_OBJECT =
  String.raw`(?!\s+(?:(?:me|us)\s+)?(?:(?:the|your|a|an)\s+)?` +
  String.raw`(?:go-?ahead|green\s+light|ok(?:ay)?|approval|permission|blessing|sign-?off|thumbs|` +
  String.raw`all\s+clear|yes|no|confirmation|consent|if|whether)\b)`;
const HANDOVER_RES: RegExp[] = [
  // "could you (please) provide / share / send ...", "would you mind sharing ..."
  new RegExp(
    String.raw`\b(?:can|could|would|will)\s+you\s+(?:please\s+|kindly\s+)?` +
      String.raw`(?:` + SUPPLY_VERBS + String.raw`|mind\s+` + SUPPLY_GERUNDS + String.raw`)` + BOOLEAN_OBJECT,
    'i',
  ),
  // "please provide / enter / paste ..."
  new RegExp(
    String.raw`\b(?:please|kindly)\s+(?:(?:just|also|then|now|simply)\s+)?` + SUPPLY_VERBS + BOOLEAN_OBJECT,
    'i',
  ),
  // "send me / give me / text me ..." (not "tell me if", "give me the go-ahead")
  new RegExp(String.raw`\b` + SUPPLY_VERBS + String.raw`(?=\s+(?:me|us)\b)` + BOOLEAN_OBJECT, 'i'),
  // "reply / respond with the code" — "reply with Yes" is still a yes/no
  /\b(?:reply|respond|answer|come\s+back)\s+(?:to\s+me\s+)?with\s+(?:the|your|a|an|that|this|it|them)\b/i,
  // "let me know the / what ..." (not "let me know if")
  new RegExp(String.raw`\b(?:let\s+(?:me|us)\s+know|tell\s+(?:me|us))\s+(?:the|your|a|an|` + WH + String.raw`)\b`, 'i'),
  // "once you have the code" / "as soon as you send it over" / "when you get it"
  new RegExp(
    String.raw`\b(?:once|when|as\s+soon\s+as|after)\s+you(?:'ve|\s+have)?\s+` +
      String.raw`(?:have|receive|received|get|got|gotten|send|sent|share|shared|provide|provided|` +
      String.raw`paste|pasted|enter|entered|forward|forwarded|reply|replied|upload|uploaded|` +
      String.raw`attach|attached|text|texted)\s+(?:the|your|a|an|it|them|that|this|me|us|over|back|through|along)\b`,
    'i',
  ),
  // "I'll need the code" (not "I need your approval")
  new RegExp(
    String.raw`\b(?:i|we)(?:'ll|\s+will|'d|\s+would|\s+still|\s+just)?\s+need\s+(?:the|your|a|an|that|this)\s+` +
      String.raw`(?!(?:approval|permission|go-?ahead|ok(?:ay)?|confirmation|sign-?off|green\s+light|blessing|` +
      String.raw`decision|answer|input\s+on|thoughts|yes|no)\b)`,
    'i',
  ),
  // "I need you to send ..." (not "I need you to confirm")
  /\bneed\s+you\s+to\s+(?!(?:confirm|approve|authori[sz]e|decide|choose|review|let\s+me\s+know\s+if|tell\s+me\s+if|say)\b)/i,
  // "waiting for the code" (not "waiting for your go-ahead")
  new RegExp(
    String.raw`\bwaiting\s+(?:for|on)\s+(?:the|your|a|an)\s+` +
      String.raw`(?!(?:approval|permission|go-?ahead|ok(?:ay)?|confirmation|sign-?off|green\s+light|` +
      String.raw`response|reply|answer|decision|word|thoughts|feedback)\b)`,
    'i',
  ),
  // "paste it here" / "enter the code below" / "share it in this chat"
  /\b(?:enter|paste|type|key|drop|share|send|post|reply|respond)\b[^.!?\n]{0,40}\b(?:here|below|in\s+(?:the|this)\s+chat|in\s+your\s+(?:reply|response|next\s+message))\b/i,
];

// A login challenge the user has to answer. In the closing sentence this alone
// stands the buttons down; elsewhere in the tail it needs PENDING_CONTEXT_RE
// on the same line ("I logged in with the 2FA code you provided" is history,
// "the verification code was sent to your phone" is a request).
const CREDENTIAL_RE = new RegExp(
  String.raw`\b(?:2[-\s]?fa|mfa|tfa|otp|one[-\s]?time\s+(?:pass)?(?:code|word|pin)|` +
    String.raw`two[-\s]?(?:factor|step)|multi[-\s]?factor|` +
    String.raw`(?:verification|verify|security|auth(?:entication|enticator|orization)?|access|login|log-in|` +
    String.raw`sign[-\s]?in|sms|text|confirmation)\s+code|` +
    String.raw`pass\s?code|pin(?:\s+(?:code|number))?|password|passphrase|` +
    String.raw`(?:secret|security)\s+(?:question|answer)|authenticator(?:\s+app)?|` +
    String.raw`(?:\d+|four|five|six|seven|eight)[-\s]?digit\s+(?:code|number|pin|otp|passcode)|` +
    String.raw`code\s+(?:(?:that\s+)?(?:was|has\s+been|is\s+being|will\s+be|just\s+got|got)\s+)?` +
    String.raw`(?:sent|texted|emailed|delivered)\s+to|` +
    String.raw`code\s+you\s+(?:just\s+)?(?:received|got|were\s+sent)|` +
    String.raw`receive(?:d)?\s+(?:a|the|your)\s+code)\b`,
  'i',
);
// "the code" without a qualifier: a request only together with pending context
// ("Do you have the code handy?"), never on its own ("class code 91580").
// Pending context = the challenge is outstanding: delivery wording ("sent to
// your phone", "number on file", "ending in 4821"), a request to hand it over,
// or a question about having it.
const GENERIC_CODE_RE = /\b(?:the|a|your|that|this|any|each|which)\s+code\b|\bcode\s*\?/i;
const PENDING_CONTEXT_RE = new RegExp(
  String.raw`\b(?:sent\s+to|was\s+sent|been\s+sent|being\s+sent|texted|emailed\s+to|on\s+its\s+way|` +
    String.raw`receive|received|check\s+your|look\s+for|asking\s+(?:you\s+)?for|prompt(?:s|ed|ing)?\s+(?:you\s+)?for|` +
    String.raw`requir(?:e|es|ed|ing)|request(?:s|ed|ing)?|needs?|needed|waiting|pending|outstanding|` +
    String.raw`once\s+you|when\s+you|as\s+soon\s+as|after\s+you|if\s+you\s+have|do\s+you\s+have|have\s+you|did\s+you|` +
    String.raw`please|kindly|handy|(?:number|phone|email|address)\s+on\s+file|ending\s+in\s+\d+|` +
    String.raw`(?:to|on)\s+your\s+(?:phone|mobile|cell(?:\s*phone)?|device|email|inbox|authenticator)|` +
    String.raw`(?:provide|enter|share|paste|send|type|forward|relay|read|give|submit|supply|input|key)\s+` +
    String.raw`(?:me|us|the|your|that|this|it|them)|reply\s+with|respond\s+with)\b`,
  'i',
);

/** The answer as the user reads it: one non-empty trimmed line per entry. */
const plainLines = (text: string): string[] =>
  text
    .replace(QUICK_PROMPT_RE, '')
    .replace(META_BLOCK_RE, '')
    .replace(ENTITY_LINK_RE, '$1')
    .replace(HTML_TAG_RE, ' ')
    .replace(MD_PUNCT_RE, '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

const splitSentences = (line: string): string[] => {
  const sentences: string[] = [];
  for (const fragment of line.replace(SENTENCE_END_RE, `$1${SENTENCE_MARK}`).split(SENTENCE_MARK)) {
    if (sentences.length && ABBREVIATION_END_RE.test(sentences[sentences.length - 1])) {
      sentences[sentences.length - 1] = `${sentences[sentences.length - 1]} ${fragment}`;
    } else {
      sentences.push(fragment);
    }
  }
  return sentences.map((sentence) => sentence.trim()).filter(Boolean);
};

const closingSentence = (lines: string[]): string => {
  const sentences = lines.length ? splitSentences(lines[lines.length - 1]) : [];
  return sentences.length ? sentences[sentences.length - 1] : '';
};

const standDownReason = (lines: string[]): string => {
  const tail = lines.slice(-TAIL_LINES);
  if (!tail.length) return '';
  const closing = closingSentence(tail);
  if (WH_QUESTION_RE.test(closing) || WH_EMBEDDED_RE.test(closing)) return 'open-ended question';
  if (ALTERNATIVES_RE.test(closing)) return 'alternatives';
  if (HOLD_RE.test(closing)) return 'asks to wait';
  if (ANYTHING_ELSE_RE.test(closing)) return 'anything else';
  if (CREDENTIAL_RE.test(closing)) return 'login challenge';
  for (const line of tail) {
    if (HANDOVER_RES.some((rx) => rx.test(line))) return 'hand-over request';
    if (PENDING_CONTEXT_RE.test(line) && (CREDENTIAL_RE.test(line) || GENERIC_CODE_RE.test(line))) return 'pending code';
    if (splitSentences(line).some((sentence) => WH_QUESTION_RE.test(sentence))) return 'open-ended question';
  }
  return '';
};

/**
 * Why a reply that closes with a go-ahead question gets no Yes / No (the rule
 * name), or "" when nothing in its closing lines asks for a typed reply.
 * Diagnostic companion to asksToProceed().
 */
export const proceedStandDownReason = (message: string | null | undefined): string =>
  standDownReason(plainLines(message ?? ''));

export const asksToProceed = (message: string | null | undefined): boolean => {
  const lines = plainLines(message ?? '');
  if (!lines.length) return false;
  if (!PROCEED_QUESTION_RE.test(closingSentence(lines))) return false;
  return !standDownReason(lines);
};

// ── Replies ──────────────────────────────────────────────────────────────────
// Sent verbatim as the user's reply, so the agent gets an unambiguous answer.
export const PROCEED_YES_REPLY = 'Yes, proceed.';
export const PROCEED_NO_REPLY = 'No, do not proceed.';

// ── Backend flag ─────────────────────────────────────────────────────────────
// The terminal answer event (responseStreaming === false) of /claude-managed/chat
// and /momentum-kanban-stream carries the backend's own decision on a go-ahead
// question (utils/proceed_prompt.py in IGIGMomentumMyAMP):
//   { show_proceed_buttons: boolean,
//     proceed_prompt: { text: string, options: { label, reply }[] } | null }
// `text` is "" for a plain closing "Do you want me to proceed?" (the question is
// already the last line of the answer) and the options are the Yes / No pair
// below. A $$SHOWMODAL_…$$ marker yields its question and labels instead.
export const DEFAULT_PROCEED_OPTIONS: QuickReplyOption[] = [
  { label: 'Yes', reply: PROCEED_YES_REPLY },
  { label: 'No', reply: PROCEED_NO_REPLY },
];

export type ApiProceedPromptEvent = {
  show_proceed_buttons?: boolean | null;
  proceed_prompt?: {
    text?: string | null;
    options?: { label?: string | null; reply?: string | null }[] | null;
  } | null;
};

/**
 * The backend's decision on a terminal answer event:
 *   - a prompt when it flagged a go-ahead question,
 *   - null when it decided there is none (no buttons, even if the text would
 *     pass asksToProceed),
 *   - undefined when the event carries no decision (older backend) — callers
 *     then fall back to parseQuickPrompt / asksToProceed on the text.
 */
export const proceedPromptFromEvent = (
  evt: ApiProceedPromptEvent | null | undefined,
): AiQuickPrompt | null | undefined => {
  if (typeof evt?.show_proceed_buttons !== 'boolean') return undefined;
  if (!evt.show_proceed_buttons) return null;
  const raw = evt.proceed_prompt;
  const options: QuickReplyOption[] = (raw?.options ?? [])
    .map((o) => ({ label: (o?.label ?? '').trim(), reply: (o?.reply ?? o?.label ?? '').trim() }))
    .filter((o) => o.label && o.reply);
  return {
    text: (raw?.text ?? '').trim(),
    options: options.length ? options : DEFAULT_PROCEED_OPTIONS,
  };
};
